import { useState, type FormEvent } from "react";
import { useApi } from "../api/context";
import { t } from "../i18n";
import { ErrorNotice } from "../shell/ErrorNotice";
import { passwordRules } from "./password-rules";
import { submitSetPassword, type SetPasswordForm } from "./set-password";
import { AuthLayout } from "./SignInFlow";

/**
 * Public: an invited staff member chooses their first password with the code
 * their invite sent by SMS, then signs in as usual. No session is involved.
 */
export function SetPasswordPage({ onDone }: { onDone: () => void }) {
  const { client } = useApi();
  const [form, setForm] = useState<SetPasswordForm>({ email: "", code: "", password: "", confirm: "" });
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const edit = (patch: Partial<SetPasswordForm>) => {
    setProblem(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    const outcome = await submitSetPassword(client, form);
    setPending(false);
    if (outcome.kind === "done") onDone();
    else if (outcome.kind === "problem") setProblem(outcome.message);
    else setError(outcome.error);
  }

  return (
    <AuthLayout>
      <SetPasswordFields
        form={form}
        onEdit={edit}
        onSubmit={submit}
        pending={pending}
        problem={problem}
        error={error}
      />
    </AuthLayout>
  );
}

/** The form itself, without state, so it can be rendered in a test. */
export function SetPasswordFields(props: {
  form: SetPasswordForm;
  onEdit: (patch: Partial<SetPasswordForm>) => void;
  onSubmit: (event: FormEvent) => void;
  pending: boolean;
  problem: string | null;
  error: unknown;
}) {
  const { form, onEdit } = props;
  const rules = passwordRules(form.email, form.password);
  return (
    <>
      <div>
        <h1>{t("setPassword.title")}</h1>
        <p className="sub">{t("setPassword.subtitle")}</p>
      </div>
      <form onSubmit={props.onSubmit} noValidate>
        <div className="field">
          <label htmlFor="sp-email">{t("setPassword.email")}</label>
          <input
            id="sp-email"
            type="email"
            autoComplete="username"
            autoFocus
            value={form.email}
            onChange={(event) => onEdit({ email: event.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="sp-code">{t("setPassword.code")}</label>
          <input
            id="sp-code"
            className="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={form.code}
            onChange={(event) => onEdit({ code: event.target.value.replace(/\D/g, "") })}
          />
        </div>
        <div className="field">
          <label htmlFor="sp-password">{t("setPassword.password")}</label>
          <input
            id="sp-password"
            type="password"
            autoComplete="new-password"
            aria-describedby="sp-rules"
            value={form.password}
            onChange={(event) => onEdit({ password: event.target.value })}
          />
        </div>
        <div id="sp-rules" className="rules" aria-live="polite">
          <span className="meta">{t("setPassword.rules")}</span>
          <ul>
            {rules.map((rule) => (
              <li key={rule.id} className={rule.met ? "met" : ""} data-rule={rule.id}>
                <span aria-hidden="true">{rule.met ? "✓" : "·"}</span> {rule.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="field">
          <label htmlFor="sp-confirm">{t("setPassword.confirm")}</label>
          <input
            id="sp-confirm"
            type="password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(event) => onEdit({ confirm: event.target.value })}
          />
        </div>
        {props.problem ? <p className="field-problem">{props.problem}</p> : null}
        {props.error ? <ErrorNotice error={props.error} context="setPassword" /> : null}
        <button className="btn primary" type="submit" disabled={props.pending}>
          {props.pending ? t("setPassword.submitting") : t("setPassword.submit")}
        </button>
        <a className="linkish" href="#">
          {t("setPassword.back")}
        </a>
      </form>
    </>
  );
}
